import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type {
  EstudioActivo,
  EstudioActivoTipo,
  EstudioLook,
  EstudioUsoIa,
  EstudioProveedorIa,
  FondoId,
  PosePreset,
  TipoOfertaEstudio,
} from '../utils/estudioDiseno/types';
import { LIMITE_CORTESIA_IA } from '../utils/estudioDiseno/types';

export interface EstudioIaConfigPublic {
  proveedor_preferido: EstudioProveedorIa;
  has_openai_key: boolean;
  has_gemini_key: boolean;
}

interface EstudioState {
  activos: EstudioActivo[];
  looks: EstudioLook[];
  uso: EstudioUsoIa | null;
  iaConfig: EstudioIaConfigPublic | null;
  isLoading: boolean;
  error: string | null;
  fetchAll: (empresaId: string) => Promise<void>;
  uploadActivo: (empresaId: string, tipo: EstudioActivoTipo, nombre: string, file: File) => Promise<EstudioActivo>;
  deleteActivo: (id: string) => Promise<void>;
  saveLook: (
    empresaId: string,
    look: {
      id?: string;
      nombre: string;
      tipo_oferta: TipoOfertaEstudio | null;
      categoria_id: string | null;
      modelo_id: string | null;
      fondo_id: string | null;
      fondo_preset: FondoId;
      poses: PosePreset[];
      imagenes_por_color: number;
      notas?: string | null;
    }
  ) => Promise<EstudioLook>;
  deleteLook: (id: string) => Promise<void>;
  saveIaConfig: (
    empresaId: string,
    patch: { proveedor_preferido?: EstudioProveedorIa; openai_api_key?: string | null; gemini_api_key?: string | null }
  ) => Promise<void>;
}

function mapLook(row: Record<string, unknown>): EstudioLook {
  const posesRaw = row.poses;
  return {
    id: String(row.id),
    empresa_id: String(row.empresa_id),
    nombre: String(row.nombre || ''),
    tipo_oferta: (row.tipo_oferta as TipoOfertaEstudio) || null,
    categoria_id: row.categoria_id ? String(row.categoria_id) : null,
    modelo_id: row.modelo_id ? String(row.modelo_id) : null,
    fondo_id: row.fondo_id ? String(row.fondo_id) : null,
    fondo_preset: (row.fondo_preset as FondoId) || 'blanco',
    poses: Array.isArray(posesRaw) ? (posesRaw as PosePreset[]) : [],
    imagenes_por_color: Number(row.imagenes_por_color) || 2,
    notas: row.notas != null ? String(row.notas) : null,
    created_at: String(row.created_at || ''),
  };
}

export const useEstudioDisenoStore = create<EstudioState>((set, get) => ({
  activos: [],
  looks: [],
  uso: null,
  iaConfig: null,
  isLoading: false,
  error: null,

  fetchAll: async (empresaId) => {
    set({ isLoading: true, error: null });
    try {
      const [activosRes, looksRes, usoRes, cfgRes] = await Promise.all([
        supabase.from('estudio_activos').select('*').eq('empresa_id', empresaId).order('created_at', { ascending: false }),
        supabase.from('estudio_looks').select('*').eq('empresa_id', empresaId).order('created_at', { ascending: false }),
        supabase.from('estudio_uso_ia').select('*').eq('empresa_id', empresaId).maybeSingle(),
        supabase
          .from('estudio_ia_config')
          .select('proveedor_preferido, openai_api_key, gemini_api_key')
          .eq('empresa_id', empresaId)
          .maybeSingle(),
      ]);
      if (activosRes.error) throw activosRes.error;
      if (looksRes.error) throw looksRes.error;

      if (!usoRes.data && !usoRes.error) {
        await supabase.from('estudio_uso_ia').insert({ empresa_id: empresaId });
      }

      const cfg = cfgRes.data as { proveedor_preferido?: string; openai_api_key?: string | null; gemini_api_key?: string | null } | null;
      set({
        activos: (activosRes.data || []) as EstudioActivo[],
        looks: (looksRes.data || []).map((r) => mapLook(r as Record<string, unknown>)),
        uso: (usoRes.data as EstudioUsoIa) || {
          empresa_id: empresaId,
          generaciones_cortesia: 0,
          limite_cortesia: LIMITE_CORTESIA_IA,
        },
        iaConfig: {
          proveedor_preferido: (cfg?.proveedor_preferido as EstudioProveedorIa) || 'cortesia',
          has_openai_key: Boolean(cfg?.openai_api_key),
          has_gemini_key: Boolean(cfg?.gemini_api_key),
        },
        isLoading: false,
      });
    } catch (e) {
      set({
        isLoading: false,
        error: e instanceof Error ? e.message : 'No se pudo cargar el estudio',
      });
    }
  },

  uploadActivo: async (empresaId, tipo, nombre, file) => {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const path = `${empresaId}/estudio/${tipo}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error: upErr } = await supabase.storage.from('productos').upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || 'image/png',
    });
    if (upErr) throw upErr;
    const { data: pub } = supabase.storage.from('productos').getPublicUrl(path);
    const { data, error } = await supabase
      .from('estudio_activos')
      .insert({ empresa_id: empresaId, tipo, nombre: nombre.trim() || tipo, imagen_url: pub.publicUrl })
      .select('*')
      .single();
    if (error) throw error;
    const activo = data as EstudioActivo;
    set({ activos: [activo, ...get().activos] });
    return activo;
  },

  deleteActivo: async (id) => {
    const { error } = await supabase.from('estudio_activos').delete().eq('id', id);
    if (error) throw error;
    set({ activos: get().activos.filter((a) => a.id !== id) });
  },

  saveLook: async (empresaId, look) => {
    const payload = {
      empresa_id: empresaId,
      nombre: look.nombre.trim(),
      tipo_oferta: look.tipo_oferta,
      categoria_id: look.categoria_id,
      modelo_id: look.modelo_id,
      fondo_id: look.fondo_id,
      fondo_preset: look.fondo_preset,
      poses: look.poses,
      imagenes_por_color: look.imagenes_por_color,
      notas: look.notas || null,
      updated_at: new Date().toISOString(),
    };
    if (look.id) {
      const { data, error } = await supabase.from('estudio_looks').update(payload).eq('id', look.id).select('*').single();
      if (error) throw error;
      const mapped = mapLook(data as Record<string, unknown>);
      set({ looks: get().looks.map((l) => (l.id === mapped.id ? mapped : l)) });
      return mapped;
    }
    const { data, error } = await supabase.from('estudio_looks').insert(payload).select('*').single();
    if (error) throw error;
    const mapped = mapLook(data as Record<string, unknown>);
    set({ looks: [mapped, ...get().looks] });
    return mapped;
  },

  deleteLook: async (id) => {
    const { error } = await supabase.from('estudio_looks').delete().eq('id', id);
    if (error) throw error;
    set({ looks: get().looks.filter((l) => l.id !== id) });
  },

  saveIaConfig: async (empresaId, patch) => {
    const current = get().iaConfig;
    const row: Record<string, unknown> = {
      empresa_id: empresaId,
      proveedor_preferido: patch.proveedor_preferido || current?.proveedor_preferido || 'cortesia',
      updated_at: new Date().toISOString(),
    };
    if (patch.openai_api_key !== undefined) row.openai_api_key = patch.openai_api_key;
    if (patch.gemini_api_key !== undefined) row.gemini_api_key = patch.gemini_api_key;
    const { error } = await supabase.from('estudio_ia_config').upsert(row, { onConflict: 'empresa_id' });
    if (error) throw error;
    set({
      iaConfig: {
        proveedor_preferido: (row.proveedor_preferido as EstudioProveedorIa) || 'cortesia',
        has_openai_key: patch.openai_api_key !== undefined ? Boolean(patch.openai_api_key) : Boolean(current?.has_openai_key),
        has_gemini_key: patch.gemini_api_key !== undefined ? Boolean(patch.gemini_api_key) : Boolean(current?.has_gemini_key),
      },
    });
  },
}));
